package net.bothosting.docs.pages.hosting_in_bot_hosting_net

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initClone_a_git_repository_on_your_serverPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("hosting_in_bot_hosting_net/clone_a_git_repository_on_your_server.md", FrontMatterElement.Builder { addScalar("description", "You may want to run code directly pulled from a Git repository on your server. Our servers provide configuration options for doing this."); addScalar("icon", "git"); }))
}

@Page("/hosting_in_bot_hosting_net/clone-a-git-repository-on-your-server")
@Layout(".components.layouts.DocsLayout")
@Composable
fun Clone_a_git_repository_on_your_serverPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("clone-a-git-repository-on-your-server") }) {
        org.jetbrains.compose.web.dom.Text("Clone a Git Repository on Your Server")
    }
    com.varabyte.kobweb.silk.components.display.Callout(type = com.varabyte.kobweb.silk.components.display.CalloutType.WARNING, label = null, variant = com.varabyte.kobweb.silk.components.display.CalloutDefaults.Variant) {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text(" If you are having trouble cloning a Git repository, you can always download the files to your PC and then upload them to the server. If you are uploading files directly from the control panel, see ")
            com.varabyte.kobweb.silk.components.navigation.Link("/hosting_in_bot_hosting_net/set-up-a-server") {
                org.jetbrains.compose.web.dom.Text("Set Up a Server")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
    }
    com.varabyte.kobweb.silk.components.display.Callout(type = com.varabyte.kobweb.silk.components.display.CalloutType.TIP, label = null, variant = com.varabyte.kobweb.silk.components.display.CalloutDefaults.Variant) {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text(" If your bot is a Java ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text(".jar")
            }
            org.jetbrains.compose.web.dom.Text(", you do not need to clone a Git repository. Just upload the ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text(".jar")
            }
            org.jetbrains.compose.web.dom.Text(" file to your server.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To clone a Git repository onto your server, configure the repository in the Startup tab.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first thing you must set is the repository address:")
    }
    com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figure") {
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("img", attrs = {attr("src", "../.gitbook/assets/image (15).png");attr("alt", "")})
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figcaption")
    }

    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is the URL of your repository, for example ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("https://github.com/yourname/reponame")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Next, you must set the install branch. For most people, this is probably ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("master")
        }
        org.jetbrains.compose.web.dom.Text(". This is the production branch of your repository that should be pulled.")
    }
    com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figure") {
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("img", attrs = {attr("src", "../.gitbook/assets/image (16).png");attr("alt", "")})
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figcaption")
    }

    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("If your repository is private, you must fill in the following variables.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You must enter your username on the Git host in the Git username variable:")
    }
    com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figure") {
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("img", attrs = {attr("src", "../.gitbook/assets/image (17).png");attr("alt", "")})
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figcaption")
    }

    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Then you must enter your personal access token. Depending on the site you use to host your repository, you can find it at the following links:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/settings/tokens") {
                org.jetbrains.compose.web.dom.Text("GitHub")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("https://gitlab.com/-/profile/personal_access_tokens") {
                org.jetbrains.compose.web.dom.Text("GitLab")
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If you use a different service to host your repository, you must create a personal access token there.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Finally...")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To make your server pull from the repository every time it restarts, you must trigger the initial clone. First, make sure there are no files on your server. Then, on the Settings page of your server, click ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Reinstall Server")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figure") {
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("img", attrs = {attr("src", "../.gitbook/assets/image (18).png");attr("alt", "")})
        com.varabyte.kobweb.compose.dom.GenericTag<org.w3c.dom.Element>("figcaption")
    }

    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Your repository should then be cloned.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("possible-problem") }) {
        org.jetbrains.compose.web.dom.Text("Possible Problem")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Issue")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Reason")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("The repository doesn't clone.")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Please make sure that you followed the guide step by step without missing anything. Do not enable/disable anything else unless you are sure of their functionality.")
                }
            }
        }
    }
}
